/**
 * @fileoverview This file implements `generateCodeWithStreamV2`, a function that generates code using AI streaming with V2 prompts. It leverages `streamChatCompletions` for AI completion, `applyProposedChangesToFilesWithModel` for applying changes, and `thinkAndGetContext` for context retrieval. The function returns the AI-generated content.
 */

import { streamChatCompletions } from '../ai/llm_api'
import { Model } from '@iterator/shared-node/ai/llm_types'
import { applyProposedChangesToFilesWithModel } from '../apply_changes'
import { ContextProviders, embeddingContextProvider, thinkAndGetContext } from '../context'
import { UndoRedo } from '../history'
import {
  debug,
  debug_block,
  info_block,
  isVerbose,
  print_ai,
  startThinkingAnimation,
} from '@iterator/shared-node/util/logging'
import { time, printTiming, Timing } from '@iterator/shared-node/util/timing'
import { RewriteWithPath } from '../rewrite'
import { SingleTonUsageTracker } from '@iterator/shared-node/util/usage_tracker'
import { GENERATE_WITH_STREAM_PROMPTS_V2, parseCodeBlocks } from './generate_v2_prompt'
import { getMarkdownFromUrl } from '../web'
import { serializeForLlm } from '@iterator/shared-common/src/serialize_for_llm'
import { AiStreamer } from '../ai_streamer'
import { baseOfSparsePath, SparsePath } from '@iterator/shared-node/util/sparse_path'

export async function generateCodeWithStreamV2(
  directory: SparsePath,
  prompt: string,
  model: Model,
  contextProviderName: keyof typeof ContextProviders = 'fileNames',
  url?: string,
  docs?: string,
  onChunk: ((chunk: string) => void) | undefined = undefined,
): Promise<string> {
  debug(
    `generateCodeWithStream: directory='${JSON.stringify(directory)}' prompt='${prompt}' url='${url}'`,
  )
  const {
    system,
    texts,
    timing: contextTiming,
  } = await time('generatContext', async () => {
    const context = await thinkAndGetContext(
      directory,
      prompt,
      true,
      ContextProviders[contextProviderName],
    )
    if (docs) {
      const docContext = await embeddingContextProvider(docs, prompt)
      info_block('Documentation context files', docContext.system)
      return {
        system: [context.system, docContext.system].join('\n'),
        texts: [...context.texts, ...docContext.texts],
      }
    }
    return context
  })

  let webContent = ''
  let getMarkdownTiming: Timing | undefined = undefined
  if (url) {
    const stopAnimation = startThinkingAnimation(`Reading ${url}...`)
    const result = await time('getMarkdownFromUrl', async () => await getMarkdownFromUrl(url))
    stopAnimation()
    if (result.success) {
      webContent = serializeForLlm.webContent(url, result.markdown)
    }
    getMarkdownTiming = result.timing
  }

  const streamer = new AiStreamer()
  const { message, timing: streamTiming } = await time('streamChatCompletions', async () => {
    return streamChatCompletions(
      `${GENERATE_WITH_STREAM_PROMPTS_V2.systemPrefix}\n\n${system}`,
      [...texts, webContent],
      [
        {
          role: 'user',
          content: prompt,
        },
      ],
      (chunk) => {
        if (!isVerbose()) {
          streamer.stream(chunk)
        }
        if (onChunk) {
          onChunk(chunk)
        }
      },
      model,
    )
  })
  if (!isVerbose()) {
    print_ai('') // Add a newline after streaming.
  } else {
    debug_block('Stream complete content', message.content)
  }

  const codeBlocks = parseCodeBlocks(message.content)
  debug_block('Code blocks', codeBlocks)
  const allSuccessfulRewrites: RewriteWithPath[] = []

  const { timing: applyTiming, rewrites: speculativeDecodingRewrites } = await time(
    'applyingChanges',
    async () => {
      let stopAnimation: () => void = () => {}
      if (!isVerbose()) {
        stopAnimation = startThinkingAnimation('Applying changes...')
      }
      const additionalRewrites = await applyProposedChangesToFilesWithModel(codeBlocks)
      stopAnimation()
      return { rewrites: additionalRewrites }
    },
  )
  allSuccessfulRewrites.push(...speculativeDecodingRewrites)

  new UndoRedo(baseOfSparsePath(directory)).record(allSuccessfulRewrites)

  printTiming(
    'generateCodeWithStreamV2',
    [contextTiming, streamTiming, applyTiming].concat(getMarkdownTiming ? [getMarkdownTiming] : []),
  )
  SingleTonUsageTracker.log('generateCodeWithStreamV2')
  return message.content
}
